import React, { useEffect } from 'react';
import ReactMarkdown from 'react-markdown';

import {
  Avatar,
  Badge,
  Button,
  Checkbox,
  Flex,
  type GetProp,
  message,
  Modal,
  Space,
  Tooltip,
  Drawer,
  Typography,
  Spin,
} from 'antd';
import { WebsocketsEventType, type CreateChatData } from '@coze/api';
import {
  Attachments,
  Bubble,
  Conversations,
  Sender,
  Welcome,
  useXAgent,
  useXChat,
} from '@ant-design/x';
import { type MessageInfo } from '@ant-design/x/es/use-x-chat';
import {
  CloudUploadOutlined,
  PaperClipOutlined,
  PhoneOutlined,
  PhoneTwoTone,
  PlayCircleFilled,
  PlusOutlined,
  MenuOutlined,
  SoundOutlined,
  MutedOutlined,
  StopOutlined,
  ShareAltOutlined,
  FontSizeOutlined,
  ZoomOutOutlined,
  SendOutlined,
  AudioOutlined,
  LoadingOutlined,
  SyncOutlined,
  LeftOutlined,
} from '@ant-design/icons';

import useWsAPI from './use-ws-api';
import useStyles from './use-style';
import useCozeAPI from './use-coze-api';
import Settings from './settings';
import logo from './logo.svg';
import { config } from './config';
import VoiceChatModal from './components/voice-chat-modal';
import defaultDoctorAvatar from '../../assets/avator.png';
// import defaultDoctorAvatar from '../../assets/avator.svg';
import backgroundImage from '../../assets/background.png';

import fontIcon from '../../assets/font.svg';
import shareIcon from '../../assets/share.svg';
import voiceIcon from '../../assets/voice.svg';
import muteIcon from '../../assets/mute.svg';
import unmuteIcon from '../../assets/sound.svg';
import sendIcon from '../../assets/send.svg';
import keyboardIcon from '../../assets/keyboard.svg';

interface Conversation {
  key: string;
  label: string;
}
const getRoles = (typing: boolean): GetProp<typeof Bubble.List, 'roles'> => ({
  ai: {
    placement: 'start',
    typing: typing ? { step: 5, interval: 20 } : false,
    styles: {
      content: {
        borderRadius: 16,
        backgroundColor: '#F6F6F6',
        color: '#000000',
        fontSize: '1em',
        fontFamily: 'SourceHanSansCN',
      },
    },
  },
  local: {
    placement: 'end',
    variant: 'shadow',
    styles: {
      content: {
        borderRadius: 16,
        backgroundColor: '#0068FF',
        color: '#FFFFFF',
        fontSize: '1em',
        fontFamily: 'SourceHanSansCN',
      },
    },
  },
});

const { Paragraph, Text } = Typography;

const DISCLAIMER_ACCEPTED_KEY = 'disclaimer_accepted';
const DEFAULT_DOCTOR_AVATAR = defaultDoctorAvatar;

const ChatX: React.FC = () => {
  // ==================== Style ====================
  const { styles } = useStyles();

  // ==================== State ====================
  const [headerOpen, setHeaderOpen] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [disclaimerVisible, setDisclaimerVisible] = React.useState(false);
  const [disclaimerChecked, setDisclaimerChecked] = React.useState(false);

  const [content, setContent] = React.useState('');

  const [conversationsItems, setConversationsItems] = React.useState<
    Conversation[]
  >([]);

  const activeKeyRef = React.useRef<string | undefined>();

  const [attachedFiles, setAttachedFiles] = React.useState<
    GetProp<typeof Attachments, 'items'>
  >([]);

  const messageMap = React.useRef<Map<string, MessageInfo<string>[]>>(
    new Map(),
  );
  const isTypingRef = React.useRef(false);
  const [speech, setSpeech] = React.useState(false);
  const [chat, setChat] = React.useState(false);
  const lastContentRef = React.useRef('');
  const [voiceChatModalOpen, setVoiceChatModalOpen] = React.useState(false);
  const [isMuted, setIsMuted] = React.useState(false);
  const [isFontLarge, setIsFontLarge] = React.useState(false);
  const [voiceInputMode, setVoiceInputMode] = React.useState(false);
  const [touchStartY, setTouchStartY] = React.useState(0);
  const [touchCurrentY, setTouchCurrentY] = React.useState(0);
  const [isCanceling, setIsCanceling] = React.useState(false);
  const touchStartYRef = React.useRef(0);
  const touchCurrentYRef = React.useRef(0);
  // 存储视频 URL，以消息 ID 为 key
  const videoUrlMapRef = React.useRef<Map<string, string>>(new Map());
  const currentMessageIdRef = React.useRef<string | null>(null);
  const pendingVideoUrlRef = React.useRef<string | null>(null);
  // 存储已展开的视频消息 ID
  const [expandedVideos, setExpandedVideos] = React.useState<Set<string>>(new Set());
  // 存储视频元素的引用，用于自动播放
  const videoElementRefMap = React.useRef<Map<string, HTMLVideoElement>>(new Map());
  // 存储缩略图视频元素的引用
  const thumbnailVideoRefMap = React.useRef<Map<string, HTMLVideoElement>>(new Map());
  // 存储视频缩略图 URL（第一帧）
  const videoThumbnailMapRef = React.useRef<Map<string, string>>(new Map());
  // 存储当前全屏播放的视频消息 ID
  const [fullscreenVideoId, setFullscreenVideoId] = React.useState<string | null>(null);
  // 全屏视频元素引用
  const fullscreenVideoRef = React.useRef<HTMLVideoElement | null>(null);
  // 防止重复触发全屏的标志
  const isExitingFullscreenRef = React.useRef(false);
  // 是否已经请求过全屏的标志
  const hasRequestedFullscreenRef = React.useRef(false);
  // 存储每个消息的打字进度（已显示字符数）
  const [typingProgress, setTypingProgress] = React.useState<Map<string, number>>(new Map());
  // 跟踪哪些消息正在打字，避免重复创建定时器
  const typingTimersRef = React.useRef<Map<string, NodeJS.Timeout>>(new Map());

  const { initClient, botInfo, getBotInfo, uploadFile, uploading, clientRef } =
    useCozeAPI();
  const {
    startChat,
    stopChat,
    startTranscriptions,
    stopTranscriptions,
    sendWsMessage,
    startSpeech,
    togglePlay,
    stopPlay,
    messageMuted,
  } = useWsAPI(clientRef, data => {
    if (data.event_type === WebsocketsEventType.TRANSCRIPTIONS_MESSAGE_UPDATE) {
      setContent(lastContentRef.current + data.data.content);
    }
  });

  // ==================== Runtime ====================
  const [agent] = useXAgent({
    request: ({ message: query }, { onUpdate, onSuccess }) => {
      const conversationId = activeKeyRef.current || '0';
      isTypingRef.current = true;

      sendWsMessage({
        query: query ?? '',
        conversationId: conversationId === '0' ? undefined : conversationId,
        onUpdate,
        onSuccess: (delta: string) => {
          onSuccess(delta);
          isTypingRef.current = false;
        },
        onVideoUrl: (url: string) => {
          // 将视频 URL 暂存，等待消息创建后关联
          const messageId = currentMessageIdRef.current;
          if (messageId) {
            // 如果已经有消息 ID，直接设置
            videoUrlMapRef.current.set(messageId, url);
            setMessages(prev => [...prev]);
          } else {
            // 否则暂存，等待 useEffect 处理
            pendingVideoUrlRef.current = url;
          }
        },
        onCreated: (data: CreateChatData) => {
          setConversationsItems(prev => {
            const exist = prev.find(
              item => item.key === data.conversation_id || item.key === '0',
            );
            activeKeyRef.current = data.conversation_id;

            if (!exist) {
              return [
                ...prev,
                {
                  key: data.conversation_id,
                  label: query ?? '',
                },
              ];
            } else {
              if (exist.key === '0') {
                const newConversationsItems = prev.map(item => {
                  if (item.key === '0') {
                    return { key: data.conversation_id, label: query ?? '' };
                  }
                  return item;
                });

                return newConversationsItems;
              }
              return prev;
            }
          });
        },
      });
    },
  });

  const { onRequest, messages, setMessages } = useXChat({
    agent,
  });

  // 暂停除指定消息ID外的所有其他视频（包括展开的视频和缩略图视频）
  const pauseOtherVideos = React.useCallback((currentMessageId: string) => {
    stopPlay();
    // 暂停展开的视频
    videoElementRefMap.current.forEach((videoElement, messageId) => {
      if (messageId !== currentMessageId && !videoElement.paused) {
        videoElement.pause();
      }
    });
    // 暂停缩略图视频
    thumbnailVideoRefMap.current.forEach((videoElement, messageId) => {
      if (messageId !== currentMessageId && !videoElement.paused) {
        videoElement.pause();
      }
    });
  }, [stopPlay]);

  // 监听消息变化，更新当前消息 ID，并处理待处理的视频 URL
  useEffect(() => {
    const lastAiMessage = messages.findLast(msg => msg.status !== 'local');
    if (lastAiMessage && lastAiMessage.id !== 'welcome-id') {
      const messageId = String(lastAiMessage.id);
      currentMessageIdRef.current = messageId;
      console.log('useEffect-messageId', messageId);
      
      // 如果有待处理的视频 URL，将其关联到当前消息
      if (pendingVideoUrlRef.current) {
        videoUrlMapRef.current.set(messageId, pendingVideoUrlRef.current);
        pendingVideoUrlRef.current = null;
        setMessages(prev => [...prev]);
      }
    }
  }, [messages, setMessages]);

  // 监听视频展开状态，自动播放展开的视频
  useEffect(() => {
    expandedVideos.forEach((messageId) => {
      const videoElement = videoElementRefMap.current.get(messageId);
      if (videoElement) {
        // 先暂停其他正在播放的视频
        pauseOtherVideos(messageId);
        // 然后播放当前视频
        videoElement.play().catch(() => {
          // 如果自动播放失败（浏览器策略），忽略错误
        });
      }
    });
  }, [expandedVideos, pauseOtherVideos]);

  // 处理打字效果
  useEffect(() => {
    // 清理已完成或已删除消息的定时器
    const messageIds = new Set(messages.map(({ id }) => String(id)));
    typingTimersRef.current.forEach((timer, messageId) => {
      if (!messageIds.has(messageId)) {
        clearInterval(timer);
        typingTimersRef.current.delete(messageId);
        setTypingProgress(prev => {
          const newMap = new Map(prev);
          newMap.delete(messageId);
          return newMap;
        });
      }
    });
    
    messages.forEach(({ id, message: msg, status }) => {
      if (status !== 'local' && typeof msg === 'string') {
        const messageId = String(id);
        const currentProgress = typingProgress.get(messageId);
        const fullText = msg;
        
        // 如果进度未定义，说明是新消息，初始化为 0
        if (currentProgress === undefined && fullText.length > 0) {
          setTypingProgress(prev => {
            const newMap = new Map(prev);
            newMap.set(messageId, 0);
            return newMap;
          });
        }
        
        // 为每个需要继续打字的消息创建循环定时器（如果还没有）
        const finalProgress = typingProgress.get(messageId) ?? 0;
        if (finalProgress < fullText.length && !typingTimersRef.current.has(messageId)) {
          const timer = setInterval(() => {
            setTypingProgress(prev => {
              const newMap = new Map(prev);
              const current = newMap.get(messageId) ?? 0;
              if (current < fullText.length) {
                newMap.set(messageId, current + 1);
              } else {
                // 如果已完成，清除定时器
                const existingTimer = typingTimersRef.current.get(messageId);
                if (existingTimer) {
                  clearInterval(existingTimer);
                  typingTimersRef.current.delete(messageId);
                }
              }
              return newMap;
            });
          }, 30); // 每 30ms 增加一个字符
          
          typingTimersRef.current.set(messageId, timer);
        }
      }
    });
    
    // 组件卸载时清理所有定时器
    return () => {
      typingTimersRef.current.forEach((timer) => {
        clearInterval(timer);
      });
      typingTimersRef.current.clear();
    };
  }, [messages, typingProgress]);

  // 当 fullscreenVideoId 变为 null 时，确保视频被完全清理
  useEffect(() => {
    if (!fullscreenVideoId) {
      // 当 fullscreenVideoId 为 null 时，确保视频被清理
      if (fullscreenVideoRef.current) {
        const video = fullscreenVideoRef.current;
        video.pause();
        video.src = '';
        video.load();
        fullscreenVideoRef.current = null;
      }
      // 重置标志
      isExitingFullscreenRef.current = false;
      hasRequestedFullscreenRef.current = false;
    } else {
      // 当进入全屏时，暂停所有非全屏视频（包括展开的视频和缩略图视频）
      pauseOtherVideos(fullscreenVideoId);
    }
  }, [fullscreenVideoId, pauseOtherVideos]);

  // 监听全屏状态变化
  useEffect(() => {
    if (!fullscreenVideoId) {
      return;
    }

    const handleFullscreenChange = () => {
      // 如果正在退出全屏，忽略全屏状态变化事件
      if (isExitingFullscreenRef.current) {
        return;
      }

      const isFullscreen = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );

      // 如果退出全屏，清理状态
      if (!isFullscreen && fullscreenVideoRef.current && !isExitingFullscreenRef.current) {
        isExitingFullscreenRef.current = true;
        // 立即清除全屏视频 ID，隐藏全屏节点
        setFullscreenVideoId(null);
        
        const video = fullscreenVideoRef.current;
        // 暂停视频
        video.pause();
        // 清除视频源，防止继续播放
        video.src = '';
        video.load(); // 重新加载以清除缓冲
        // 移除全屏相关属性
        video.setAttribute('webkit-playsinline', 'true');
        video.setAttribute('playsinline', 'true');
        video.removeAttribute('x5-video-player-fullscreen');
        
        fullscreenVideoRef.current = null;
        hasRequestedFullscreenRef.current = false;
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, [fullscreenVideoId]);

  useEffect(() => {
    // 检查是否已经同意声明
    const accepted = localStorage.getItem(DISCLAIMER_ACCEPTED_KEY);
    if (!accepted || true) {
      setDisclaimerVisible(true);
    }

    const baseUrl = config.getBaseUrl();
    const pat = config.getPat();
    const botId = config.getBotId();

    if (baseUrl && pat && botId) {
      initClient();
      getBotInfo();
    } else {
      // message.error('Please set the base URL, PAT, bot ID and voice file ID');
    }
  }, []);

  // ==================== Event ====================
  const onSubmit = (nextContent: string) => {
    if (!nextContent) {
      return;
    }
    onRequest(nextContent);
    setContent('');
  };

  const onAddConversation = () => {
    if (conversationsItems.find(item => item.key === '0')) {
      message.error("Don't create more than one empty conversation");
      return;
    }
    if (activeKeyRef.current) {
      messageMap.current.set(activeKeyRef.current, messages);
    }

    setConversationsItems(prev => [
      ...prev,
      {
        key: '0',
        label: `新对话 ${conversationsItems.length}`,
      },
    ]);
    activeKeyRef.current = '0';
    setMessages([]);
  };

  const onConversationClick: GetProp<
    typeof Conversations,
    'onActiveChange'
  > = key => {
    if (activeKeyRef.current) {
      messageMap.current.set(activeKeyRef.current, messages);
    }

    activeKeyRef.current = key;

    if (messageMap.current.has(key)) {
      setMessages(messageMap.current.get(key) || []);
    } else {
      setMessages([]);
    }
  };

  const handleFileChange: GetProp<typeof Attachments, 'onChange'> = info => {
    setAttachedFiles(info.fileList);
    uploadFile(info.fileList[0]?.originFileObj as File);
  };

  const onSettingsChange = () => {
    initClient();
    getBotInfo();
  };

  // ==================== Nodes ====================
  const placeholderNode = (
    <Space direction="vertical" size={16} className={styles.placeholder}>
      {/* <Welcome
        variant="borderless"
        icon={botInfo?.icon_url}
        title={botInfo?.name}
        description={botInfo?.description}
        extra={
          <Space>
          </Space>
        }
      /> */}
    </Space>
  );

  // 打字机效果组件
  const TypewriterText = React.useCallback(({ 
    messageId, 
    text, 
    speed = 30 
  }: { 
    messageId: string; 
    text: string; 
    speed?: number;
  }) => {
    const [displayedText, setDisplayedText] = React.useState('');
    const progress = typingProgress.get(messageId) ?? 0;

    React.useEffect(() => {
      if (progress >= text.length) {
        setDisplayedText(text);
        return;
      }

      const timer = setTimeout(() => {
        const nextProgress = Math.min(progress + 1, text.length);
        setTypingProgress(prev => {
          const newMap = new Map(prev);
          newMap.set(messageId, nextProgress);
          return newMap;
        });
        setDisplayedText(text.slice(0, nextProgress));
      }, speed);

      return () => clearTimeout(timer);
    }, [progress, text, messageId, speed]);

    React.useEffect(() => {
      // 初始化显示
      if (progress === 0 && text.length > 0) {
        setTypingProgress(prev => {
          const newMap = new Map(prev);
          newMap.set(messageId, 0);
          return newMap;
        });
      }
    }, [messageId, text]);

    return <span>{displayedText}</span>;
  }, [typingProgress]);

  // 思考中的省略号动画组件
  const ThinkingDots = () => (
    <span style={{ display: 'inline-block', width: '40px' }}>
      <span
        style={{
          display: 'inline-block',
          animation: 'thinkingDot 1.4s infinite',
          animationDelay: '0s',
        }}
      >
        .
      </span>
      <span
        style={{
          display: 'inline-block',
          animation: 'thinkingDot 1.4s infinite',
          animationDelay: '0.2s',
        }}
      >
        .
      </span>
      <span
        style={{
          display: 'inline-block',
          animation: 'thinkingDot 1.4s infinite',
          animationDelay: '0.4s',
        }}
      >
        .
      </span>
      <style>
        {`
          @keyframes thinkingDot {
            0%, 60%, 100% {
              opacity: 0.3;
              transform: translateY(0);
            }
            30% {
              opacity: 1;
              transform: translateY(-4px);
            }
          }
        `}
      </style>
    </span>
  );

  const items: GetProp<typeof Bubble.List, 'items'> = [
    ...messages.map(
      ({ id, message: msg, status }) => {
        const messageId = String(id);
        const isAssistant = status !== 'local';
        const videoUrl = videoUrlMapRef.current.get(messageId);
        
        // 对于 assistant 的字符串消息，应用打字效果
        let displayText = typeof msg === 'string' ? msg : '';
        if (isAssistant && typeof msg === 'string') {
          const progress = typingProgress.get(messageId);
          // 如果进度未定义，显示空文本（useEffect 会初始化）
          // 如果进度已定义，使用进度值显示部分文本
          if (progress === undefined) {
            displayText = '';
          } else {
            displayText = msg.slice(0, progress);
          }
        }
        
        const markdownContent = typeof msg === 'string' ? (
          <ReactMarkdown
            components={{
              // 保持代码块样式
              code: ({ className, children, ...props }: any) => {
                const isInline = !className;
                return isInline ? (
                  <code
                    style={{
                      backgroundColor: 'rgba(0, 0, 0, 0.1)',
                      padding: '2px 4px',
                      borderRadius: 3,
                      fontFamily: 'monospace',
                      fontSize: '0.9em',
                    }}
                    {...props}
                  >
                    {children}
                  </code>
                ) : (
                  <code
                    style={{
                      display: 'block',
                      backgroundColor: 'rgba(0, 0, 0, 0.05)',
                      padding: '8px 12px',
                      borderRadius: 4,
                      overflow: 'auto',
                      fontSize: '0.9em',
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                    }}
                    {...props}
                  >
                    {children}
                  </code>
                );
              },
              // 保持链接样式
              a: ({ ...props }: any) => (
                <a
                  {...props}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: 'inherit',
                    textDecoration: 'underline',
                  }}
                />
              ),
              // 保持段落样式
              p: ({ ...props }: any) => (
                <p style={{ margin: '0 0 4px 0' }} {...props} />
              ),
            }}
          >
            {displayText}
          </ReactMarkdown>
        ) : (
          msg
        );

        const isExpanded = expandedVideos.has(messageId);
        
        // 检查打字效果是否完成（仅对 assistant 的字符串消息）
        const isTypingComplete = isAssistant && typeof msg === 'string' 
          ? (() => {
              const progress = typingProgress.get(messageId);
              return progress !== undefined && progress >= msg.length;
            })()
          : true; // 非 assistant 消息或非字符串消息，视为已完成
        
        const content = videoUrl ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {markdownContent && (
              <div style={{ wordBreak: 'break-word' }}>{markdownContent}</div>
            )}
            {isExpanded ? (
              <video
                ref={(el) => {
                  if (el) {
                    videoElementRefMap.current.set(messageId, el);
                  } else {
                    videoElementRefMap.current.delete(messageId);
                  }
                }}
                src={videoUrl}
                controls
                autoPlay
                style={{
                  maxWidth: '100%',
                  borderRadius: 8,
                  backgroundColor: '#000',
                }}
                preload="metadata"
                onCanPlay={(e) => {
                  // 视频加载完成后自动播放
                  const video = e.target as HTMLVideoElement;
                  // 先暂停其他正在播放的视频
                  pauseOtherVideos(messageId);
                  video.play().catch(() => {
                    // 如果自动播放失败（浏览器策略），忽略错误
                  });
                }}
                onPlay={(e) => {
                  // 当视频开始播放时，暂停其他正在播放的视频
                  pauseOtherVideos(messageId);
                }}
              >
                您的浏览器不支持视频播放。
              </video>
            ) : isTypingComplete ? (
              <div
                onClick={() => {
                  setFullscreenVideoId(messageId);
                }}
                style={{
                  width: 200,
                  height: 200,
                  borderRadius: 8,
                  backgroundColor: '#000',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <video
                  src={videoUrl}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                  preload="metadata"
                  webkit-playsinline="true" // 兼容旧版微信内核
                  x5-playsinline="true" 
                  // autoPlay
                  poster={`https://zqagent.awakewise.com/thumbnail/?url=${encodeURIComponent(videoUrl)}`}
                  // preload={/iPhone/i.test(navigator.userAgent) && /MicroMessenger/i.test(navigator.userAgent) ? "auto" : "metadata"}
                  muted
                  playsInline
                />
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <PlayCircleFilled style={{ fontSize: 32, color: '#000' }} />
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          <div style={{ wordBreak: 'break-word' }}>{markdownContent}</div>
        );

        return {
          key: id,
          role: status === 'local' ? 'local' : 'ai',
          content,
          style: status === 'local' 
            ? {  color: '#FFFFFF', borderRadius: 16 , fontSize: isFontLarge ? 20 : 18 } 
            : {  color: '#000000', borderRadius: 16 , fontSize: isFontLarge ? 20 : 18 }, 
          // footer: (
          //   <Flex>
          //     <Button
          //       size="small"
          //       type="text"
          //       icon={<PlayCircleFilled />}
          //       style={{ marginInlineEnd: 'auto' }}
          //       onClick={() => {
          //         startSpeech(msg);
          //         // if (getIsSpeech()) {
          //         //   stopSpeech();
          //         // } else {
          //         //   startSpeech(msg);
          //         // }
          //       }}
          //     />
          //     <Button
          //       size="small"
          //       type="text"
          //       icon={<PlayCircleFilled />}
          //       style={{ marginInlineEnd: 'auto' }}
          //       onClick={() => {
          //         togglePlay();
          //       }}
          //     />
          //   </Flex>
          // ),
        };
      },
    ),
    // 当正在请求时，添加思考提示消息
    ...(agent.isRequesting()
      ? [
          {
            key: 'thinking',
            role: 'ai' as const,
            content: (
              <span>
                正在思考
                <ThinkingDots />
              </span>
            ),
            style: {
              color: '#000000',
              borderRadius: 16,
              fontSize: isFontLarge ? 20 : 18,
            },
          },
        ]
      : []),
  ];

  const attachmentsNode = (
    <>
      {/* <Badge dot={attachedFiles.length > 0 && !headerOpen}>
        <Button
          type="text"
          icon={<PaperClipOutlined />}
          onClick={() => setHeaderOpen(!headerOpen)}
        />
      </Badge> */}
      {/* <Tooltip title="Voice Chat" >
        <Button
          type="text"
          icon={chat ? <PhoneTwoTone /> : <PhoneOutlined />}
          onClick={() => setVoiceChatModalOpen(true)}
        />
      </Tooltip> */}
    </>
  );

  const senderHeader = (
    <Sender.Header
      title="Attachments"
      open={headerOpen}
      onOpenChange={setHeaderOpen}
      styles={{
        content: {
          padding: 0,
        },
      }}
    >
      <Attachments
        beforeUpload={() => false}
        items={attachedFiles}
        onChange={handleFileChange}
        placeholder={type =>
          type === 'drop'
            ? { title: 'Drop file here' }
            : {
                icon: <CloudUploadOutlined />,
                title: 'Upload files',
                description: 'Click or drag files to this area to upload',
              }
        }
      />
    </Sender.Header>
  );

  const logoNode = (
    <div className={styles.logo}>
      <img src={logo} draggable={false} alt="logo" />
      <span style={{ padding: 5 }}>对话</span>
    </div>
  );

  const voiceChatModalNode = (
    <VoiceChatModal
      open={voiceChatModalOpen}
      onClose={() => {
        setVoiceChatModalOpen(false);
        if (chat) {
          stopChat().then(() => {
            setChat(false);
            message.info('Stop Voice Chat');
          });
        }
      }}
      recording={chat}
      onRecordingChange={async nextChat => {
        if (nextChat) {
          await startChat();
          message.info('Start Voice Chat');
        } else {
          await stopChat();
          message.info('Stop Voice Chat');
        }
        setChat(nextChat);
      }}
    />
  );

  const handleDisclaimerAgree = () => {
    if (!disclaimerChecked) {
      message.warning('请先阅读并勾选"已阅读并知悉"');
      return;
    }
    localStorage.setItem(DISCLAIMER_ACCEPTED_KEY, 'true');
    setDisclaimerVisible(false);
    
    // 添加欢迎消息
    if (botInfo?.onboarding_info?.prologue) {
      const prologue = botInfo.onboarding_info.prologue;
      const suggestedQuestions = botInfo.onboarding_info.suggested_questions || [];
      
      // 创建欢迎消息内容
      const welcomeContent = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ wordBreak: 'break-word' }}>{prologue}</div>
          {suggestedQuestions.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {suggestedQuestions.map((question, index) => (
                <Button
                  key={index}
                  type="default"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRequest(question);
                  }}
                  style={{
                    textAlign: 'left',
                    whiteSpace: 'normal',
                    height: 'auto',
                    padding: '8px 16px',
                    borderRadius: 8,
                    border: '1px solid #d9d9d9',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                    e.currentTarget.style.borderColor = '#0068FF';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#fff';
                    e.currentTarget.style.borderColor = '#d9d9d9';
                  }}
                >
                  {question}
                </Button>
              ))}
            </div>
          )}
        </div>
      );
      
      // 添加欢迎消息到消息列表
      const welcomeMessageId = `welcome-id`;
      setMessages(prev => [
        ...prev,
        {
          id: welcomeMessageId,
          message: welcomeContent,
          status: 'ai',
        } as any,
      ]);
      
      // 朗读欢迎消息
      if (!isMuted) {
        startSpeech(prologue);
      }
    }
  };

  const handleDisclaimerDisagree = () => {
    setDisclaimerVisible(false);
    message.info('您需要同意声明才能使用本服务');
  };

  const disclaimerModalNode = (
    <Modal
      title={
        <Text strong style={{ fontSize: 18 }}>
          重要提示及声明
        </Text>
      }
      open={disclaimerVisible}
      onCancel={handleDisclaimerDisagree}
      closable={false}
      maskClosable={false}
      footer={null}
      width={600}
      style={{padding: 20}}
      centered
    >
      <Space direction="vertical" size="middle" style={{ width: '100%', marginTop: 16, maxHeight: '250px', overflowY: 'scroll'}}>
        <Paragraph style={{ margin: 0 }}>
          <Text strong>1. 医学参考定位</Text>
          <br />
          <Text type="secondary">
            本AI数字医生提供的医学信息和回答仅供参考，不作为诊断、用药依据，不能替代面对面医疗专业人员的诊疗。
          </Text>
        </Paragraph>

        <Paragraph style={{ margin: 0 }}>
          <Text strong>2. 技术与应用风险</Text>
          <br />
          <Text type="secondary">
            由于个体医学差异、沟通限制及AI模型固有局限，服务内容可能存在错误或遗漏。西京医院-甲乳血管外科不保证信息的准确性、可靠性、及时性或完整性，用户需自行承担决策风险，医院不承担法律责任。
          </Text>
        </Paragraph>

        <Paragraph style={{ margin: 0 }}>
          <Text strong>3. 服务适用范围</Text>
          <br />
          <Text type="secondary">
            本服务仅提供医学健康相关内容，不涉及社会意识形态、动物疾病、违反法律伦理或医学司法证据。
          </Text>
        </Paragraph>

        <Paragraph style={{ margin: 0 }}>
          <Text strong>4. 专业医疗建议</Text>
          <br />
          <Text type="secondary">
            如有医学诊断或其他专业意见需求，请咨询专业医疗人员，并在其指导下进行决策。
          </Text>
        </Paragraph>
      </Space>
      <Space direction="vertical" size="middle" style={{ width: '100%', marginTop: 16 }}>
        <Space style={{ marginTop: 16 }}>
          <Checkbox
            checked={disclaimerChecked}
            onChange={e => setDisclaimerChecked(e.target.checked)}
          >
            已阅读并知悉
          </Checkbox>
          <Typography.Link
            onClick={() => {
              // 这里可以添加打开知情同意书的逻辑
              message.info('知情同意书功能待实现');
            }}
          >
            知情同意书
          </Typography.Link>
        </Space>

        <Flex justify="flex-end" gap="small" style={{ marginTop: 24 }}>
          <Button onClick={handleDisclaimerDisagree}>不同意</Button>
          <Button type="primary" onClick={handleDisclaimerAgree}>
            同意并继续
          </Button>
        </Flex>
      </Space>
    </Modal>
  );

  // 全屏视频播放器节点
  const fullscreenVideoNode = fullscreenVideoId ? (() => {
    const videoUrl = videoUrlMapRef.current.get(fullscreenVideoId);
    if (!videoUrl) return null;

    const handleExitFullscreen = () => {
      // 设置退出标志，防止全屏状态监听器重复处理
      isExitingFullscreenRef.current = true;
      
      // 立即清除全屏视频 ID，隐藏全屏节点
      setFullscreenVideoId(null);
      
      // 停止视频播放并清理
      if (fullscreenVideoRef.current) {
        const video = fullscreenVideoRef.current;
        // 暂停视频
        video.pause();
        // 清除视频源，防止继续播放
        video.src = '';
        video.load(); // 重新加载以清除缓冲
        // 对于 iOS，需要移除全屏相关属性，防止自动重新全屏
        video.setAttribute('webkit-playsinline', 'true');
        video.setAttribute('playsinline', 'true');
        video.removeAttribute('x5-video-player-fullscreen');
        fullscreenVideoRef.current = null;
      }
      
      // 退出浏览器全屏状态
      const exitFullscreen = 
        document.exitFullscreen ||
        (document as any).webkitExitFullscreen ||
        (document as any).mozCancelFullScreen ||
        (document as any).msExitFullscreen;
      
      if (exitFullscreen) {
        exitFullscreen.call(document).catch(() => {
          // 如果退出全屏失败，忽略错误
        });
      }
      
      // 重置标志
      hasRequestedFullscreenRef.current = false;
    };

    const handleRequestFullscreen = (videoElement: HTMLVideoElement) => {
      // 尝试全屏播放（支持各种浏览器和微信环境）
      const requestFullscreen = 
        videoElement.requestFullscreen ||
        (videoElement as any).webkitRequestFullscreen ||
        (videoElement as any).webkitEnterFullscreen ||
        (videoElement as any).mozRequestFullScreen ||
        (videoElement as any).msRequestFullscreen;

      if (requestFullscreen) {
        requestFullscreen.call(videoElement).catch(() => {
          // 如果全屏失败，仍然显示全屏覆盖层
        });
      }
    };

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#000',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* 返回按钮 */}
        <Button
          type="text"
          icon={<LeftOutlined />}
          onClick={handleExitFullscreen}
          style={{
            position: 'absolute',
            top: 16,
            left: 16,
            zIndex: 10000,
            color: '#fff',
            fontSize: 24,
            width: 48,
            height: 48,
            minWidth: 48,
            minHeight: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            borderRadius: '50%',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
          }}
          onTouchStart={(e) => {
            // 移动端触摸反馈
            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
          }}
          onTouchEnd={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.6)';
          }}
        />
        {/* 视频播放器 */}
        <video
          ref={(el) => {
            if (el && !isExitingFullscreenRef.current) {
              fullscreenVideoRef.current = el;
              // 设置微信等环境的全屏属性
              el.setAttribute('webkit-playsinline', 'false');
              el.setAttribute('playsinline', 'false');
              el.setAttribute('x5-playsinline', 'false');
              el.setAttribute('x5-video-player-type', 'h5');
              el.setAttribute('x5-video-player-fullscreen', 'true');
              
              // 先暂停其他视频
              pauseOtherVideos(fullscreenVideoId);
              // 播放当前视频
              el.play().catch(() => {
                // 如果自动播放失败，忽略错误
              });
              // 尝试全屏（延迟一下确保视频已加载，且只请求一次）
              if (!hasRequestedFullscreenRef.current) {
                hasRequestedFullscreenRef.current = true;
                setTimeout(() => {
                  // 再次检查是否仍在全屏模式，避免在退出过程中触发
                  if (!isExitingFullscreenRef.current && fullscreenVideoId) {
                    handleRequestFullscreen(el);
                  }
                }, 100);
              }
            } else if (!el) {
              // 当元素被卸载时，清理引用
              fullscreenVideoRef.current = null;
            }
          }}
          src={videoUrl}
          controls
          autoPlay
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
          playsInline={false}
          onPlay={() => {
            // 播放时暂停其他视频
            if (!isExitingFullscreenRef.current) {
              pauseOtherVideos(fullscreenVideoId);
            }
          }}
        >
          您的浏览器不支持视频播放。
        </video>
      </div>
    );
  })() : null;

  // ==================== Render =================
  const menuContent = (
    <>
      {/* 🌟 Logo */}
      {logoNode}
      {/* 🌟 Add Conversation */}
      <Button
        onClick={() => {
          onAddConversation();
          setMenuOpen(false);
        }}
        type="link"
        className={styles.addBtn}
        icon={<PlusOutlined />}
      >
        新对话
      </Button>
      {/* 🌟 Conversation Management */}
      <Conversations
        items={conversationsItems}
        className={styles.conversations}
        activeKey={activeKeyRef.current}
        onActiveChange={key => {
          onConversationClick(key);
          setMenuOpen(false);
        }}
      />
    </>
  );

  const headerNode = (
    <div className={styles.topHeader}>
      <Flex align="center" gap={12}>
        <Avatar
          size={40}
          src={DEFAULT_DOCTOR_AVATAR || botInfo?.icon_url}
          style={{
            backgroundColor: '#f0f0f0',
            border: '2px solid #e0e0e0',
          }}
        >
          医生
        </Avatar>
        <Text 
          style={{ 
            fontSize: 18, 
            color: '#000000', 
            fontWeight: 400, 
            fontFamily: '975MaruSC, sans-serif',
            fontStyle: 'normal',
            letterSpacing: 3,
          }}
        >
          王廷医生
        </Text>
      </Flex>
      <Space size={4} className="headerActions">
        <Tooltip>
          <Button
            type="text"
            icon={isMuted ? <img src={muteIcon} alt="mute" style={{ width: 20, height: 20 }} /> : <img src={unmuteIcon} alt="unmute" style={{ width: 20, height: 20 }} />}
            onClick={() => {
              setIsMuted(!isMuted);
              if(!isMuted) {
                stopPlay();
                messageMuted(true);
              } else {
                messageMuted(false);
              }
              message.info(isMuted ? '已取消静音' : '已静音');
            }}
          />
        </Tooltip>
        
        <Tooltip title={isFontLarge ? '还原字体' : '放大字体'}>
          <Button
            type="text"
            // icon={isFontLarge ? <FontSizeOutlined /> : <FontSizeOutlined />}
            icon={<img src={fontIcon} alt="font" style={{ width: 20, height: 20 }} />}
            onClick={() => {
              setIsFontLarge(!isFontLarge);
              message.info(isFontLarge ? '字体已还原' : '字体已放大');
            }}
          />
        </Tooltip>

        <Tooltip>
          <Button
            type="text"
            icon={<img src={shareIcon} alt="share" style={{ width: 20, height: 20 }} />}
            onClick={async () => {
              try {
                const currentUrl = window.location.href;
                await navigator.clipboard.writeText(currentUrl);
                message.success('链接已复制到剪贴板');
              } catch (err) {
                // 降级方案：使用传统方法
                const currentUrl = window.location.href;
                const textArea = document.createElement('textarea');
                textArea.value = currentUrl;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                document.body.appendChild(textArea);
                textArea.select();
                try {
                  document.execCommand('copy');
                  message.success('链接已复制到剪贴板');
                } catch (error) {
                  message.error('复制失败，请手动复制链接');
                }
                document.body.removeChild(textArea);
              }
            }}
          />
        </Tooltip>
      </Space>
    </div>
  );

  return (
    <div 
      className={styles.layout}
      style={{
        backgroundImage: `url(${backgroundImage})`,
        // fontSize: isFontLarge ? '1.2857em' : '1em', // 18px / 14px = 1.2857
      }}
    >
      {/* 顶部头部 */}
      {headerNode}
      
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}>
        {/* 移动端菜单按钮 */}
        {/* <Button
          type="primary"
          icon={<MenuOutlined />}
          className={styles.menuButton}
          onClick={() => setMenuOpen(true)}
        /> */}
        
        {/* 移动端抽屉 */}
        {/* <Drawer
          title={null}
          placement="left"
          onClose={() => setMenuOpen(false)}
          open={menuOpen}
          closable={false}
          width={280}
          styles={{
            body: {
              padding: 0,
            },
          }}
          className={styles.menuDrawer}
          maskClosable
        >
          {menuContent}
        </Drawer> */}
        
        {/* 桌面端固定菜单 */}
        {/* <div className={styles.menu}>
          {menuContent}
        </div> */}
        
        <div className={styles.chat}>
        {/* 🌟 Message List */}
        <Bubble.List
          items={
            items.length > 0
              ? items
              : [{ content: placeholderNode, variant: 'borderless' }]
          }
          roles={getRoles(isTypingRef.current)}
          className={styles.messages}
        />
        {voiceInputMode ? (
          <>
            {/* 录音时的全屏遮罩 */}
            {speech && (
              <div 
                className={styles.recordingOverlay}
                onContextMenu={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  return false;
                }}
                onTouchStart={(e) => {
                  if (e.cancelable) {
                    e.preventDefault();
                  }
                }}
                onTouchMove={(e) => {
                  if (e.cancelable) {
                    e.preventDefault();
                  }
                  const touch = e.touches[0];
                  if (touch) {
                    const currentY = touch.clientY;
                    setTouchCurrentY(currentY);
                    touchCurrentYRef.current = currentY;
                    const deltaY = touchStartYRef.current - currentY;
                    // 如果向上滑动超过50px，显示取消状态
                    if (deltaY > 50) {
                      setIsCanceling(true);
                    } else {
                      setIsCanceling(false);
                    }
                  }
                }}
                onTouchEnd={async (e) => {
                  if (e.cancelable) {
                    e.preventDefault();
                  }
                  if (speech) {
                    const deltaY = touchStartYRef.current - touchCurrentYRef.current;
                    await stopTranscriptions();
                    setSpeech(false);
                    setIsCanceling(false);
                    
                    if (deltaY > 50) {
                      // 上滑取消
                      setContent(lastContentRef.current);
                      message.info('已取消');
                    } else {
                      // 松开发送
                      setTimeout(() => {
                        const finalContent = content.trim();
                        if (finalContent) {
                          onSubmit(finalContent);
                          setContent('');
                        }
                      }, 200);
                    }
                    // 重置触摸位置
                    setTouchStartY(0);
                    setTouchCurrentY(0);
                    touchStartYRef.current = 0;
                    touchCurrentYRef.current = 0;
                  }
                }}
              >
                <div className={styles.recordingContent}>
                  <div className={styles.recordingHint}>
                    {isCanceling ? '松开取消' : '松手发送，上移取消'}
                  </div>
                  <div className={styles.waveform}>
                    {Array.from({ length: 50 }).map((_, i) => (
                      <div
                        key={i}
                        className={styles.waveBar}
                        style={{
                          animationDelay: `${i * 0.05}s`,
                          opacity: isCanceling ? 0.3 : 1,
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {/* 语音输入栏 */}
            <div 
              className={styles.voiceInputBar}
              onTouchStart={async (e) => {
                if (!speech) {
                  const touch = e.touches[0];
                  const startY = touch.clientY;
                  setTouchStartY(startY);
                  setTouchCurrentY(startY);
                  touchStartYRef.current = startY;
                  touchCurrentYRef.current = startY;
                  setIsCanceling(false);
                  stopPlay();
                  lastContentRef.current = content;
                  await startTranscriptions();
                  setSpeech(true);
                }
              }}
              onTouchMove={(e) => {
                if (speech) {
                  const touch = e.touches[0];
                  if (touch) {
                    const currentY = touch.clientY;
                    setTouchCurrentY(currentY);
                    touchCurrentYRef.current = currentY;
                    const deltaY = touchStartYRef.current - currentY;
                    // 如果向上滑动超过50px，显示取消状态
                    if (deltaY > 50) {
                      setIsCanceling(true);
                    } else {
                      setIsCanceling(false);
                    }
                  }
                }
              }}
              onTouchEnd={async (e) => {
                if (speech) {
                  const deltaY = touchStartYRef.current - touchCurrentYRef.current;
                  await stopTranscriptions();
                  setSpeech(false);
                  setIsCanceling(false);
                  
                  if (deltaY > 50) {
                    // 上滑取消
                    setContent(lastContentRef.current);
                    message.info('已取消');
                  } else {
                    // 松开发送
                    setTimeout(() => {
                      const finalContent = content.trim();
                      if (finalContent) {
                        onSubmit(finalContent);
                        setContent('');
                      }
                    }, 200);
                  }
                  // 重置触摸位置
                  setTouchStartY(0);
                  setTouchCurrentY(0);
                  touchStartYRef.current = 0;
                  touchCurrentYRef.current = 0;
                }
              }}
            >
              <div 
              className={speech ? styles.recordingText : ''}
              style={!speech ? { 
                flex: 1, 
                textAlign: 'center', 
                fontSize: 20, 
                fontWeight: 'bold', 
                color: '#000000',
                fontFamily: 'SourceHanSansCN, sans-serif', 
                fontStyle: 'normal', 
                letterSpacing: 3 
              } : undefined}
            >
              {speech ? '正在录音...' : '按住说话'}
            </div>
            {agent.isRequesting() ? <SyncOutlined spin /> : <img src={keyboardIcon} alt="keyboard" 
              style={{ width: 25, height: 25, color: '#000000', cursor: 'pointer' }}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              onMouseUp={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              onTouchStart={(e) => {
                e.stopPropagation();
              }}
              onTouchEnd={(e) => {
                e.stopPropagation();
              }}
              onClick={async (e) => {
                e.stopPropagation();
                e.preventDefault();
                if (speech) {
                  await stopTranscriptions();
                  setSpeech(false);
                }
                setVoiceInputMode(false);
              }}
            />}
          </div>
          </>
        ) : (
          <Sender
            value={content}
            header={senderHeader}
            onSubmit={onSubmit}
            onChange={setContent}
            prefix={attachmentsNode}
            loading={agent.isRequesting() || uploading}
            className={styles.sender}
            placeholder='发消息或按住说话...'
            allowSpeech={{
              // When setting `recording`, the built-in speech recognition feature will be disabled
              recording: speech,
              onRecordingChange: async nextSpeech => {
                if (nextSpeech) {
                  lastContentRef.current = content;
                  // message.info('Start Voice to Text');
                  await startTranscriptions();
                } else {
                  // message.info('Stop Voice to Text');
                  await stopTranscriptions();
                }
                setSpeech(nextSpeech);
              },
            }}
            actions={[
             
            <Button 
              key="voice"
              icon={<img src={voiceIcon} alt="voice" style={{ width: 25, height: 25 }} />} 
              style={{ marginLeft: 4 ,background: '#00000000', border: 'none'}}
              onClick={() => {
                setVoiceInputMode(true);
              }}
            />,
            <Button 
              key="send"
              icon={agent.isRequesting() ? <SyncOutlined spin /> : <img src={sendIcon} alt="send" style={{ width: 25, height: 25 }} />} 
              style={{ marginLeft: 4,background: '#00000000', border: 'none'}}
              onClick={() => {
                onSubmit(content);
              }}
            />,
            ]}
          />
        )}
        </div>
      </div>
      {voiceChatModalNode}
      {disclaimerModalNode}
      {fullscreenVideoNode}
    </div>
  );
};

export default ChatX;
